import { Box } from "@mui/material";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FilterByOptions, PaginationOptions } from "../../../../../components/common/BriaTable/BriaTable";
import { useAppStore } from "../../../../../hooks/useStores";
import TableLayout, { TableRow } from "../../../../../layout/TableLayout/TableLayout";
import { formatDate } from "../../../../../utils";
import { TgDataset } from "../../../Datasets/store/tg-dataset-store";
import TgDatasetActions from "./Dashboard/Actions/TgDatasetActions";
import TgProjectDatasetsEmpty from "./Empty/TgProjectDatasetsEmpty";
import styles from "./TgProjectDatasets.module.scss";

type TgDatasetTableRow = {
	name: string | ReactElement;
	created: string | ReactElement;
	status: string | ReactElement;
	imagesCount: string | ReactElement;
	uploadType: string | ReactElement;
	actions: ReactElement;
};

const TgProjectDatasets = () => {
	const { t } = useTranslation("tg", { keyPrefix: "datasets.dashboard" });
	const { projectId } = useParams();
	const { tgStore, authStore } = useAppStore();
	const { tgProjectStore } = tgStore;
	const deletedDraftIdsRef = useRef<Set<number>>(new Set());
	const [loadingRow, setLoadingRow] = useState<{ data: TgDataset; column: string }>();
	const tableHeaderArray = [
		t("table.headers.name"),
		t("table.headers.created"),
		t("table.headers.status"),
		t("table.headers.images_count"),
		t("table.headers.uploadType"),
		t("table.headers.actions"),
	];

	// Client-side pagination
	const [datasets, setDatasets] = useState<TgDataset[]>([]);
	const [paginatedDatasets, setPaginatedDatasets] = useState<TgDataset[]>([]);
	const [datasetsMap, setDatasetsMap] = useState<Map<number, TgDataset[]>>(new Map());

	const paginateIntoMap = (items: TgDataset[]): Map<number, TgDataset[]> => {
		const pageMap = new Map<number, TgDataset[]>();
		const totalPages = Math.ceil(items.length / tgProjectStore.rowsPerPage);
		for (let page = 1; page <= totalPages; page++) {
			const start = (page - 1) * tgProjectStore.rowsPerPage;
			pageMap.set(page, items.slice(start, start + tgProjectStore.rowsPerPage));
		}
		return pageMap;
	};

	const loadNextPage = async (page?: number | undefined, _filterBy?: FilterByOptions | undefined) => {
		setPaginatedDatasets(datasetsMap.get(page ?? 1) ?? []);
	};

	const paginationOptions: PaginationOptions = {
		rowsPerPage: tgProjectStore.rowsPerPage,
		totalRows: datasets.length,
		loadNextPage,
	};

	useEffect(() => {
		tgProjectStore.getDatasets(Number(projectId), 1, undefined, 0);
	}, [authStore.isLoadingOrgSubscriptions]);

	useEffect(() => {
		const items = tgProjectStore.paginatedDatasets.items || [];
		if (items.length) {
			const filtered = items.filter((d) => !(d.upload_type === "advanced" && d.status === "Draft"));
			setDatasets(filtered);
			const map = paginateIntoMap(filtered);
			setDatasetsMap(map);
			setPaginatedDatasets(map.get(1) ?? []);
		} else {
			setDatasets([]);
			setDatasetsMap(new Map());
			setPaginatedDatasets([]);
		}
	}, [tgProjectStore.paginatedDatasets.items, tgProjectStore.rowsPerPage]);

	// Deleting advanced upload type datasets with draft status
	useEffect(() => {
		const items = tgProjectStore.paginatedDatasets.items || [];
		items
			.filter(
				(d) => d.upload_type === "advanced" && d.status === "Draft" && !deletedDraftIdsRef.current.has(d.id),
			)
			.forEach(async (d) => {
				deletedDraftIdsRef.current.add(d.id);
				try {
					await tgStore.tgDatasetStore.deleteDataset(d.id);
				} catch (e) {
					// no-op
				}
			});
	}, [tgProjectStore.paginatedDatasets.items, tgStore]);

	const getDatasetStatus = (dataset: TgDataset) => {
		if (dataset.upload_type === "advanced" && dataset.status === "Completed" && !dataset.models?.length) {
			return "Draft";
		}
		return dataset.status;
	};

	const createRow = (dataset: TgDataset): TableRow<TgDatasetTableRow> => {
		const name = (
			<Box className={styles.nameContainer}>
				<Box className={styles.nameContainer}>
					<Box>{dataset.name}</Box>
					{dataset.description && <Box className={styles.description}>{dataset.description}</Box>}
				</Box>
			</Box>
		);

		const created = <Box>{formatDate(dataset.created_at)}</Box>;

		const status = (
			<Box className={styles.statusContainer}>
				<Box
					className={`${styles.statusDot} ${
						getDatasetStatus(dataset) === "Completed"
							? styles.statusDotPublished
							: getDatasetStatus(dataset) === "Failed"
							? styles.statusDotFailed
							: styles.statusDotDraft
					}`}
				/>
				{t(`table.status.${getDatasetStatus(dataset)}`)}
			</Box>
		);

		const uploadType = <Box className={styles.uploadType}>{dataset.upload_type}</Box>;

		const imagesCount = <Box>{dataset.images_count}</Box>

		const actions = <TgDatasetActions {...{ dataset, setLoadingRow }} />;

		const newRow: TableRow<TgDatasetTableRow> = {
			key: dataset.id,
			data: {
				name,
				created,
				status,
				imagesCount,
				uploadType,
				actions,
			},
		};

		return newRow;
	};

	const tableData = paginatedDatasets.map((dataset) => createRow(toJS(dataset)));

	return (
		<TableLayout
			headerArray={tableHeaderArray}
			tableData={tableData}
			emptyStateComponent={<TgProjectDatasetsEmpty />}
			loading={tgProjectStore.loadingProjectDatasets}
			paginationOptions={paginationOptions}
			loadingRow={loadingRow}
			hideHeader
		/>
	);
};

export default observer(TgProjectDatasets);
